import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ExcelJS from 'exceljs';
import { toast } from 'react-toastify';
import { chinaOrderImportService } from '../../../../service/admin/chinaOrderImportService';
import { fetchAllRowsPaged } from '../ActionModals';
import './style.css';
import '../style.css';

const STATUS_OPTIONS = [
  'Correct Product',
  'Main Item Missing',
  'Incomplete',
  'Different Item',
  'Damage Item',
  'Less QTY',
];

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const INDIA_SNO_FILTER_OPTIONS = [
  { value: '', label: 'All India S.No.' },
  { value: 'bshopy', label: 'F - bshopy' },
  { value: 'myntra', label: 'MT - myntra' },
  { value: 'dropshipper', label: 'BS - dropshipper' },
  { value: 'amazon', label: 'Amazon' },
  { value: 'custom', label: 'Custom prefix' },
];
const SOURCE_FILTER_OPTIONS = [
  { value: '', label: 'All Source' },
  { value: 'Yeky', label: 'Yeky' },
  { value: 'Sunny', label: 'Sunny' },
  { value: 'custom', label: 'Custom' },
];
const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'ordered', label: 'ordered' },
  { value: 'cancel', label: 'cancel' },
  { value: 'ordermade', label: 'ordermade' },
  { value: 'Shipped', label: 'Shipped' },
  { value: 'return back', label: 'return back' },
  { value: '__NULL__', label: 'null' },
  { value: 'custom', label: 'Custom' },
];

/** All persisted order-detail fields + IN Comments computed column (matches current API / schema). */
const IN_COMMENTS_EXPORT_COLS = [
  { header: 'Source', key: 'source' },
  { header: 'China S.No.', key: 'chinaSNo' },
  { header: 'India S. No.', key: 'indiaSNo' },
  { header: 'Entry Date', key: 'entryDate' },
  { header: 'Order ID', key: 'orderId' },
  { header: 'Qty', key: 'qty' },
  { header: 'Prod Name', key: 'prodName' },
  { header: 'Order Comments', key: 'orderComments' },
  { header: 'URL', key: 'url' },
  { header: 'Purchase Qty.', key: 'purchaseQty' },
  { header: 'Unit', key: 'unit' },
  { header: 'Variant', key: 'variant' },
  { header: 'Unit Price', key: 'unitPrice' },
  { header: 'Qty Price', key: 'qtyPrice' },
  { header: 'Local Shipping', key: 'localShipping' },
  { header: 'Total Price', key: 'totalPrice' },
  { header: 'Mask ID', key: 'maskId' },
  { header: 'Shipping Date', key: 'shippingDate' },
  { header: 'Shipping Comments', key: 'shippingCompany' },
  { header: 'Qty Shipped', key: 'qtyShipped' },
  { header: 'Status (import)', key: 'status' },
  { header: 'HSN', key: 'hsn' },
  { header: 'Estimated DOA', key: 'estimatedDOA' },
  { header: 'Qty Received', key: 'qtyReceived' },
  { header: 'Product Status', key: 'productStatus' },
  { header: 'Qty Pending', key: 'qtyPending' },
  { header: 'Internal Notes', key: 'internalNotes' },
];

function getInCommentsExportCellValue(row, key) {
  if (key === 'qtyPending') {
    const shipped = Number(row.qtyShipped) || 0;
    const received = Number(row.qtyReceived) || 0;
    return shipped - received;
  }
  const v = row[key];
  if (v == null || v === '') return '';
  if (v instanceof Date) return v.toISOString();
  if (typeof v === 'object') return String(v);
  return v;
}

function normalizeQtyReceivedPayload(raw) {
  if (raw === '' || raw == null) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : String(raw).trim();
}

function buildInCommentsPayload(row) {
  return {
    qtyReceived: normalizeQtyReceivedPayload(row.qtyReceived),
    productStatus: row.productStatus?.trim() ? row.productStatus.trim() : null,
    internalNotes: row.internalNotes?.trim() ? row.internalNotes.trim() : null,
  };
}

function rowFromServerRecord(updated) {
  return {
    ...updated,
    qtyReceived: updated.qtyReceived ?? '',
    productStatus: updated.productStatus ?? '',
    internalNotes: updated.internalNotes ?? '',
  };
}

/** Normalized triple for dirty-checking (must match display binding). */
function inCommentsSnapshot(row) {
  return {
    qtyReceived: String(row.qtyReceived ?? '').trim(),
    productStatus: String(row.productStatus ?? '').trim(),
    internalNotes: String(row.internalNotes ?? '').trim(),
  };
}

function inCommentsSnapshotsEqual(a, b) {
  return (
    a.qtyReceived === b.qtyReceived && a.productStatus === b.productStatus && a.internalNotes === b.internalNotes
  );
}

function isInCommentsRowDirty(row, savedById) {
  const saved = savedById[row._id];
  if (!saved) return false;
  return !inCommentsSnapshotsEqual(inCommentsSnapshot(row), saved);
}

export default function INCommentsDashboard() {
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });
  const [rowSaving, setRowSaving] = useState({});
  const [updateAllSaving, setUpdateAllSaving] = useState(false);
  /** Last saved values per row (from server) for qty received / product status / internal notes */
  const [savedSnapshots, setSavedSnapshots] = useState({});

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [indiaSNoType, setIndiaSNoType] = useState('');
  const [indiaSNoCustomPrefix, setIndiaSNoCustomPrefix] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSourceCustom, setFilterSourceCustom] = useState('');
  const [filterStatusCustom, setFilterStatusCustom] = useState('');
  const hasCustomFilter = indiaSNoType === 'custom' || filterSource === 'custom' || filterStatus === 'custom';
  const hasAnyActiveFilter = Boolean(
    startDate ||
    endDate ||
    indiaSNoType ||
    indiaSNoCustomPrefix ||
    filterSource ||
    filterSourceCustom ||
    filterStatus ||
    filterStatusCustom
  );

  const [isExporting, setIsExporting] = useState(false);
  const [showExportProgressModal, setShowExportProgressModal] = useState(false);
  const [exportProgressPercent, setExportProgressPercent] = useState(0);
  const [exportProgressLabel, setExportProgressLabel] = useState('');

  const EXPORT_PAGE_SIZE = 5000;
  const FETCH_PHASE_MAX_PCT = 88;

  const fetchRecords = useCallback(
    async (
      page = 1,
      searchVal = '',
      limit = pagination.limit,
      sd = startDate,
      ed = endDate,
      indiaType = indiaSNoType,
      indiaPrefix = indiaSNoCustomPrefix,
      src = filterSource,
      stat = filterStatus,
      srcCustom = filterSourceCustom,
      statCustom = filterStatusCustom
    ) => {
      setIsLoading(true);
      try {
        const resolvedSource = src === 'custom' ? String(srcCustom || '').trim() : src;
        const resolvedStatus = stat === 'custom' ? String(statCustom || '').trim() : stat;
        const res = await chinaOrderImportService.getInCommentsOrderDetails({
          page,
          limit,
          search: searchVal,
          startDate: sd,
          endDate: ed,
          indiaSNoType: indiaType,
          indiaSNoCustomPrefix: indiaType === 'custom' ? indiaPrefix : '',
          source: resolvedSource,
          status: resolvedStatus,
        });
        if (res?.status?.code === 200) {
          const fetchedData = res.data.records || [];
          const sortedData = fetchedData.sort((a, b) => {
            const dateA = a.entryDate ? new Date(a.entryDate).getTime() : 0;
            const dateB = b.entryDate ? new Date(b.entryDate).getTime() : 0;
            return dateB - dateA;
          });

          const mappedData = sortedData.map((record) => ({
            ...record,
            qtyReceived: record.qtyReceived ?? '',
            productStatus: record.productStatus ?? '',
            internalNotes: record.internalNotes ?? '',
          }));

          setRecords(mappedData);
          const snaps = {};
          mappedData.forEach((r) => {
            snaps[r._id] = inCommentsSnapshot(r);
          });
          setSavedSnapshots(snaps);
          setPagination((prev) => ({ ...prev, ...res.data.pagination }));
        } else {
          toast.error(res?.error?.message || 'Failed to load records.');
        }
      } catch {
        toast.error('Failed to load Order Details records.');
      } finally {
        setIsLoading(false);
      }
    },
    [pagination.limit, startDate, endDate, indiaSNoType, indiaSNoCustomPrefix, filterSource, filterStatus, filterSourceCustom, filterStatusCustom]
  );

  useEffect(() => {
    fetchRecords(1, '', 20);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    fetchRecords(1, searchInput, pagination.limit, startDate, endDate, indiaSNoType, indiaSNoCustomPrefix, filterSource, filterStatus);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    fetchRecords(1, '', pagination.limit, startDate, endDate, indiaSNoType, indiaSNoCustomPrefix, filterSource, filterStatus);
  };

  const handleLimitChange = (e) => {
    const newLimit = parseInt(e.target.value, 10);
    setPagination((prev) => ({ ...prev, limit: newLimit }));
    fetchRecords(1, search, newLimit, startDate, endDate, indiaSNoType, indiaSNoCustomPrefix, filterSource, filterStatus);
  };

  const handlePageChange = (newPage) =>
    fetchRecords(newPage, search, pagination.limit, startDate, endDate, indiaSNoType, indiaSNoCustomPrefix, filterSource, filterStatus);

  const handleDateFilter = () =>
    fetchRecords(1, search, pagination.limit, startDate, endDate, indiaSNoType, indiaSNoCustomPrefix, filterSource, filterStatus, filterSourceCustom, filterStatusCustom);

  const handleClearDates = () => {
    setStartDate('');
    setEndDate('');
    setIndiaSNoType('');
    setIndiaSNoCustomPrefix('');
    setFilterSource('');
    setFilterSourceCustom('');
    setFilterStatus('');
    setFilterStatusCustom('');
    fetchRecords(1, search, pagination.limit, '', '', '', '', '', '', '', '');
  };

  const handleManualChange = (id, field, value) => {
    setRecords((prev) => prev.map((row) => (row._id === id ? { ...row, [field]: value } : row)));
  };

  const handleRowUpdate = async (row) => {
    setRowSaving((s) => ({ ...s, [row._id]: true }));
    try {
      const res = await chinaOrderImportService.updateInCommentsOrderRecord(row._id, buildInCommentsPayload(row));
      if (res?.status?.code === 200) {
        const updated = res.data.record;
        const merged = rowFromServerRecord(updated);
        setRecords((prev) => prev.map((r) => (r._id === row._id ? { ...r, ...merged } : r)));
        setSavedSnapshots((prev) => ({ ...prev, [row._id]: inCommentsSnapshot(merged) }));
        toast.success('Updated.');
      } else {
        toast.error(res?.error?.message || 'Update failed.');
      }
    } catch {
      toast.error('Update failed.');
    } finally {
      setRowSaving((s) => {
        const next = { ...s };
        delete next[row._id];
        return next;
      });
    }
  };

  /** Saves Qty received, product status, and internal notes for every dirty row on the current page. */
  const handleUpdateAll = async () => {
    const dirtyRows = records.filter((r) => isInCommentsRowDirty(r, savedSnapshots));
    if (!dirtyRows.length) {
      toast.info('No unsaved changes on this page.');
      return;
    }
    setUpdateAllSaving(true);
    let ok = 0;
    let fail = 0;
    const failures = [];
    const working = records.map((r) => ({ ...r }));
    const byId = (id) => working.findIndex((r) => r._id === id);
    const succeededIds = new Set();
    try {
      for (const row of dirtyRows) {
        try {
          const res = await chinaOrderImportService.updateInCommentsOrderRecord(row._id, buildInCommentsPayload(row));
          if (res?.status?.code === 200) {
            ok += 1;
            const merged = rowFromServerRecord(res.data.record);
            const idx = byId(row._id);
            if (idx >= 0) working[idx] = { ...working[idx], ...merged };
            succeededIds.add(row._id);
          } else {
            fail += 1;
            failures.push(row.chinaSNo || row.orderId || String(row._id).slice(-6));
          }
        } catch {
          fail += 1;
          failures.push(row.chinaSNo || row.orderId || String(row._id).slice(-6));
        }
      }
      setRecords(working);
      setSavedSnapshots((prev) => {
        const next = { ...prev };
        working.forEach((r) => {
          if (succeededIds.has(r._id)) next[r._id] = inCommentsSnapshot(r);
        });
        return next;
      });
      if (fail === 0) {
        toast.success(`Updated ${ok} row(s).`);
      } else {
        toast.warning(
          `Saved ${ok}, failed ${fail}. ${failures.slice(0, 5).join(', ')}${failures.length > 5 ? '…' : ''}`
        );
      }
    } finally {
      setUpdateAllSaving(false);
    }
  };

  const anyDirtyOnPage = useMemo(
    () => records.some((r) => isInCommentsRowDirty(r, savedSnapshots)),
    [records, savedSnapshots]
  );

  const handleExport = async () => {
    setIsExporting(true);
    setShowExportProgressModal(true);
    setExportProgressPercent(2);
    setExportProgressLabel('Starting export…');
    try {
      const { rows, total } = await fetchAllRowsPaged({
        fetchPage: (params) => chinaOrderImportService.getInCommentsOrderDetails(params),
        pageSize: EXPORT_PAGE_SIZE,
        extraQuery: {
          search,
          startDate,
          endDate,
          indiaSNoType,
          indiaSNoCustomPrefix: indiaSNoType === 'custom' ? indiaSNoCustomPrefix : '',
          source: filterSource === 'custom' ? String(filterSourceCustom || '').trim() : filterSource,
          status: filterStatus === 'custom' ? String(filterStatusCustom || '').trim() : filterStatus,
        },
        onProgress: ({ loaded, total: t, page, totalPages }) => {
          const denom = Math.max(1, t);
          const pct = Math.min(
            FETCH_PHASE_MAX_PCT,
            Math.max(5, Math.round((loaded / denom) * FETCH_PHASE_MAX_PCT))
          );
          setExportProgressPercent(pct);
          setExportProgressLabel(`Loaded ${loaded} of ${t} row(s) — page ${page} / ${totalPages}`);
        },
      });

      if (total === 0 || rows.length === 0) {
        toast.warn('No data to export.');
        setShowExportProgressModal(false);
        return;
      }

      setExportProgressLabel('Building Excel file…');
      setExportProgressPercent(92);

      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet('IN Comments');
      sheet.addRow(IN_COMMENTS_EXPORT_COLS.map((c) => c.header));
      sheet.getRow(1).font = { bold: true };
      rows.forEach((row) => {
        sheet.addRow(IN_COMMENTS_EXPORT_COLS.map((c) => getInCommentsExportCellValue(row, c.key)));
      });

      IN_COMMENTS_EXPORT_COLS.forEach((col, idx) => {
        const colNum = idx + 1;
        let maxLen = col.header.length;
        sheet.getColumn(colNum).eachCell({ includeEmpty: false }, (cell) => {
          const len = cell.value != null ? String(cell.value).length : 0;
          if (len > maxLen) maxLen = len;
        });
        const wide = ['url', 'orderComments', 'internalNotes', 'prodName', 'variant'].includes(col.key);
        const cap = wide ? 60 : 50;
        sheet.getColumn(colNum).width = Math.min(Math.max(maxLen + 2, 10), cap);
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'IN_Comments_OrderDetails_Export.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);

      setExportProgressPercent(100);
      setExportProgressLabel('Export complete');
      toast.success(`Exported ${rows.length} record(s).`);
      setTimeout(() => setShowExportProgressModal(false), 500);
    } catch (e) {
      toast.error(e?.message || 'Export failed.');
      setShowExportProgressModal(false);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="od-container">
      <div className="od-header">
        <h1>IN Comments</h1>
      </div>

      <div className="cu-toolbar cu-toolbar-wrap cu-toolbar-compact">
        <form className="cu-search-bar" onSubmit={handleSearch}>
          <input
            type="text"
            className="cu-search-input"
            placeholder="Search by China S.No., India S.No., Order ID, Mask ID..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {searchInput && (
            <button type="button" className="cu-clear-btn" onClick={handleClearSearch}>
              &times;
            </button>
          )}
          <button type="submit" className="cu-btn cu-btn-search">
            Search
          </button>
        </form>

        <div className="cu-date-filter">
          <select
            className="cu-select"
            value={indiaSNoType}
            onChange={(e) => {
              const next = e.target.value;
              setIndiaSNoType(next);
              if (next !== 'custom') {
                setIndiaSNoCustomPrefix('');
                fetchRecords(1, search, pagination.limit, startDate, endDate, next, '', filterSource, filterStatus, filterSourceCustom, filterStatusCustom);
              }
            }}
          >
            {INDIA_SNO_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value || 'all-india-sno'} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {indiaSNoType === 'custom' && (
            <input
              type="text"
              className="cu-date-input"
              placeholder="Prefix"
              value={indiaSNoCustomPrefix}
              onChange={(e) => setIndiaSNoCustomPrefix(e.target.value)}
            />
          )}
        </div>

        {/* Source filter */}
        <div className="cu-date-filter">
          <select
            className="cu-select"
            value={filterSource}
            onChange={(e) => {
              const next = e.target.value;
              setFilterSource(next);
              if (next !== 'custom') {
                setFilterSourceCustom('');
                fetchRecords(1, search, pagination.limit, startDate, endDate, indiaSNoType, indiaSNoCustomPrefix, next, filterStatus, '', filterStatusCustom);
              }
            }}
          >
            {SOURCE_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value || 'all-source'} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {filterSource === 'custom' && (
            <input
              type="text"
              className="cu-date-input"
              placeholder="Source"
              value={filterSourceCustom}
              onChange={(e) => setFilterSourceCustom(e.target.value)}
            />
          )}
        </div>

        {/* Status filter */}
        <div className="cu-date-filter">
          <select
            className="cu-select"
            value={filterStatus}
            onChange={(e) => {
              const next = e.target.value;
              setFilterStatus(next);
              if (next !== 'custom') {
                setFilterStatusCustom('');
                fetchRecords(1, search, pagination.limit, startDate, endDate, indiaSNoType, indiaSNoCustomPrefix, filterSource, next, filterSourceCustom, '');
              }
            }}
          >
            {STATUS_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value || 'all-status'} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {filterStatus === 'custom' && (
            <input
              type="text"
              className="cu-date-input"
              placeholder="Status"
              value={filterStatusCustom}
              onChange={(e) => setFilterStatusCustom(e.target.value)}
            />
          )}
        </div>

        <div className="cu-date-filter">
          <label className="cu-label">Entry Date</label>
          <input
            type="date"
            className="cu-date-input"
            value={startDate}
            onChange={(e) => {
              const next = e.target.value;
              setStartDate(next);
              fetchRecords(1, search, pagination.limit, next, endDate, indiaSNoType, indiaSNoCustomPrefix, filterSource, filterStatus, filterSourceCustom, filterStatusCustom);
            }}
          />
          <label className="cu-label">To</label>
          <input
            type="date"
            className="cu-date-input"
            value={endDate}
            onChange={(e) => {
              const next = e.target.value;
              setEndDate(next);
              fetchRecords(1, search, pagination.limit, startDate, next, indiaSNoType, indiaSNoCustomPrefix, filterSource, filterStatus, filterSourceCustom, filterStatusCustom);
            }}
          />
          {hasCustomFilter && (
            <button
              type="button"
              className="cu-btn cu-btn-search cu-btn-apply"
              onClick={handleDateFilter}
              style={{ borderRadius: 'var(--cu-radius)' }}
            >
              Apply
            </button>
          )}
          {hasAnyActiveFilter && (
            <button type="button" className="cu-btn cu-btn-refresh" onClick={handleClearDates}>
              ✕ Clear
            </button>
          )}
        </div>

        <div className="cu-toolbar-right">
          <label className="cu-label">Show</label>
          <select className="cu-select" value={pagination.limit} onChange={handleLimitChange}>
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <label className="cu-label">per page</label>
          <span className="cu-total-badge">{pagination.total} total</span>
          <button
            type="button"
            className="cu-btn cu-btn-export"
            disabled={isLoading || updateAllSaving || isExporting}
            onClick={handleExport}
          >
            {isExporting ? 'Exporting…' : 'Export'}
          </button>
          <button
            type="button"
            className="cu-btn ic-btn-update-all"
            title="Save Qty received, product status, and internal notes for every row on this page"
            disabled={isLoading || updateAllSaving || records.length === 0 || !anyDirtyOnPage}
            onClick={handleUpdateAll}
          >
            {updateAllSaving ? 'Updating all…' : 'Update all'}
          </button>
          <button
            type="button"
            className="cu-btn cu-btn-refresh"
            disabled={updateAllSaving}
            onClick={() =>
              fetchRecords(
                pagination.page,
                search,
                pagination.limit,
                startDate,
                endDate,
                indiaSNoType,
                indiaSNoCustomPrefix
              )
            }
          >
            ↻
          </button>
        </div>
      </div>

      <div className="table-container od-table-wrapper" style={{ overflowX: 'auto' }}>
        {isLoading ? (
          <div className="cu-loading">Loading...</div>
        ) : (
          <table className="custom-dashboard-table">
            <thead>
              <tr>
                <th className="bg-green">China S.No.</th>
                <th>India S. No</th>
                <th className="ic-col-order-id">Order Id</th>
                <th className="ic-col-variant">Variant name</th>
                <th className="bg-red">Purchase Qty</th>
                <th>Unit</th>
                <th className="bg-yellow">Qty Shipped</th>
                <th>Mask Id</th>
                <th>Qty Received</th>
                <th>Product Status</th>
                <th>Qty Pending</th>
                <th className="ic-col-internal-notes">Internal notes</th>
                <th className="ic-col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {records.length > 0 ? (
                records.map((row) => {
                  const shipped = Number(row.qtyShipped) || 0;
                  const received = Number(row.qtyReceived) || 0;
                  const qtyPending = shipped - received;
                  const pv = row.productStatus != null ? String(row.productStatus) : '';
                  const statusUnknown = pv && !STATUS_OPTIONS.includes(pv);
                  const saving = !!rowSaving[row._id];
                  const dirty = isInCommentsRowDirty(row, savedSnapshots);

                  return (
                    <tr key={row._id}>
                      <td>{row.chinaSNo || '-'}</td>
                      <td>{row.indiaSNo || '-'}</td>
                      <td className="ic-col-order-id">{row.orderId || '-'}</td>
                      <td className="ic-col-variant">{row.variant || '-'}</td>
                      <td>{row.purchaseQty ?? '-'}</td>
                      <td>{row.unit || '-'}</td>
                      <td>{row.qtyShipped ?? '-'}</td>
                      <td>{row.maskId || '-'}</td>
                      <td>
                        <input
                          type="number"
                          min={0}
                          value={row.qtyReceived}
                          onChange={(e) => handleManualChange(row._id, 'qtyReceived', e.target.value)}
                          className="table-input"
                        />
                      </td>
                      <td>
                        <select
                          value={pv}
                          onChange={(e) => handleManualChange(row._id, 'productStatus', e.target.value)}
                          className="table-select"
                        >
                          <option value="">Select</option>
                          {statusUnknown ? <option value={pv}>{pv}</option> : null}
                          {STATUS_OPTIONS.map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className={qtyPending > 0 ? 'text-danger text-bold' : ''}>{qtyPending}</td>
                      <td className="ic-col-internal-notes">
                        <input
                          type="text"
                          value={row.internalNotes}
                          placeholder="Add notes..."
                          onChange={(e) => handleManualChange(row._id, 'internalNotes', e.target.value)}
                          className="table-input table-input-notes"
                        />
                      </td>
                      <td className="ic-col-actions">
                        <button
                          type="button"
                          className="ic-btn-update"
                          disabled={!dirty || saving || updateAllSaving}
                          onClick={() => handleRowUpdate(row)}
                        >
                          {saving ? 'Saving…' : 'Update'}
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="13" className="od-empty" style={{ textAlign: 'center', padding: '20px' }}>
                    {search ? `No records found for "${search}".` : 'No data available.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {showExportProgressModal && (
        <div className="cu-export-progress-overlay">
          <div className="cu-export-progress-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cu-export-progress-header">
              <h2>Export Progress</h2>
            </div>
            <div className="cu-export-progress-body">
              <p className="cu-export-progress-label">{exportProgressLabel}</p>
              <div className="cu-export-progress-track">
                <div className="cu-export-progress-fill" style={{ width: `${exportProgressPercent}%` }} />
              </div>
              <div className="cu-export-progress-pct">{exportProgressPercent}%</div>
            </div>
          </div>
        </div>
      )}

      {pagination.totalPages > 1 && (
        <div className="cu-pagination">
          <button className="cu-page-btn" disabled={pagination.page <= 1} onClick={() => handlePageChange(1)}>
            «
          </button>
          <button
            className="cu-page-btn"
            disabled={pagination.page <= 1}
            onClick={() => handlePageChange(pagination.page - 1)}
          >
            ‹ Prev
          </button>
          <span className="cu-page-info">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            className="cu-page-btn"
            disabled={pagination.page >= pagination.totalPages}
            onClick={() => handlePageChange(pagination.page + 1)}
          >
            Next ›
          </button>
          <button
            className="cu-page-btn"
            disabled={pagination.page >= pagination.totalPages}
            onClick={() => handlePageChange(pagination.totalPages)}
          >
            »
          </button>
        </div>
      )}
    </div>
  );
}
